import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [error, setError] = useState("");

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  // Refresh user payload (and thus society status) when any mutation returned
  // a 403 "Société fermée" → makes the red read-only banner appear immediately.
  useEffect(() => {
    const onClosed = () => { refreshMe(); };
    window.addEventListener("rimmoget:society-closed", onClosed);
    return () => window.removeEventListener("rimmoget:society-closed", onClosed);
  }, [refreshMe]);

  // Any 401 from the API means the session token has expired. Reset the
  // in-memory user so ProtectedRoute bounces the user back to /login and
  // show a toast so they know why they were logged out — but only if a
  // user was actually authenticated (silently ignore cold-boot 401s).
  useEffect(() => {
    const onExpired = async () => {
      // Best-effort server-side purge of the HTTP-only cookies so a
      // subsequent /auth/me can't silently resurrect the session.
      try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
      setUser((prev) => {
        if (prev && typeof prev === "object") {
          import("sonner").then(({ toast }) => {
            toast.info("Votre session a expiré. Veuillez vous reconnecter.", {
              id: "session-expired",
            });
          });
        }
        return false;
      });
    };
    window.addEventListener("rimmoget:session-expired", onExpired);
    return () => window.removeEventListener("rimmoget:session-expired", onExpired);
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("rimmoget_token", data.token);
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("rimmoget_token");
    localStorage.removeItem("rimmoget_impersonate_id");
    localStorage.removeItem("rimmoget_impersonate_name");
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, error, login, logout, refreshMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
